import {SerializedStyles} from '@emotion/react';
import {Theme} from '@mui/material';
import {PayloadAction, Store} from '@reduxjs/toolkit';
import {Action as HistoryAction, Location} from 'history';
import * as React from 'react';
import {IntlShape} from 'react-intl';
import {NavigateFunction, Params} from 'react-router-dom';
import {Action, AnyAction, Reducer} from 'redux';

import {GetHierarchyIds} from '../containers/enum';

export type StateId = string;

export interface MetaWithStateId {
  stateId: StateId;
}

export type FilteredPayloadAction<T = void> = PayloadAction<T, string, MetaWithStateId>;

export interface ActionWithStateId extends Action, MetaWithStateId {}

export interface AnyActionWithStateId extends ActionWithStateId {
  // Allows any extra properties to be defined in an action.
  [extraProps: string]: any;
}

export type ReducerPredicate<A extends Action = AnyAction> = (action: A) => boolean;

export type CreateFilteredReducer<S = any, A extends Action = AnyAction> = (
  reducerFunction: Reducer<S, A>,
  reducerPredicate: ReducerPredicate<A>
) => Reducer<S, A>;

export type ClickItem = string | number | Record<string, any>;

export type LocaleMessagesType = Record<string, string>;

export const APPLICATION_LOCALES = ['en', 'sk', 'cs', 'de', 'pl'] as const;
export type ApplicationLocalesTuple = typeof APPLICATION_LOCALES;
export type ApplicationLocale = ApplicationLocalesTuple[number];

export function isApplicationLocale(locale: ApplicationLocale | string): locale is ApplicationLocale {
  return APPLICATION_LOCALES.some((appLocale) => appLocale === locale);
}

export interface LanguageMessages {
  locale: ApplicationLocale;
  messages: LocaleMessagesType;
}

export type LanguagesPackage = LanguageMessages[];

export type LocaleMessagesByLocales = {
  [index in keyof ApplicationLocale]: LocaleMessagesType;
};

export interface LabelValue<T, L extends string | undefined = string | undefined> {
  label: L;
  value: T;
}

export type ConfigObjectType = Record<string, any>;

export interface LocationChangePayload {
  action: HistoryAction;
  location: Location;
}

export interface LocationState {
  locationParsed: boolean;
  reload: boolean;
}

export interface PaginatedResponse<T = any> {
  content: T[];
  number: number;
  size: number;
  totalElements: number;
  last: boolean;
  totalPages: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  first: boolean;
  numberOfElements: number;
  empty: boolean;
}

export interface PageableParams {
  page: number;
  size: number;
  sort: string[];
}

export interface DataLoader {
  apiUrl: string;
  component: React.ElementType;
  id?: string;
  useLocale?: boolean | string;
  isHierarchical?: boolean;
}

export interface CoreDataLoaders {
  enums: DataLoader[];
  translations: DataLoader[];
}

export interface RenderingServices {
  intl?: IntlShape;
  store?: Store;
  navigate?: NavigateFunction;
  routeParams?: Readonly<Params<string>>;
}

export interface TranslateEnumParams {
  enumName: string;
  enumValueName: string;
}

export interface DlSelectOption<T = unknown> {
  value: T;
  label: string;
}

export interface DlRadioSelectOption<T = unknown> {
  name: T;
  label: string;
}

export type ApplicationName = 'HAIDI' | 'MERIE';

type GetStylesParams = Record<string, string | number | boolean | undefined | any>;

export interface GetStylesOptions {
  theme?: Theme;
  params?: GetStylesParams;
}

export type GetStylesFunction = (options?: GetStylesOptions) => Record<string, SerializedStyles>;

export type DlSelectSelectedSingleValue = string | number | Record<string, any>;

export type DlSelectSelectedMultiValue = any[];

export type DlSelectSelectedValue = DlSelectSelectedMultiValue | DlSelectSelectedSingleValue;
